import {type AddToCartFormData, type AddToCartHookProps, useAddToCartForm} from "../../hooks/useAddToCartNew";
import {useLayoutEffect, useState} from "react";
import {getNetPrice} from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import {FormProvider} from "react-hook-form";
import {LoadingModal} from "./Modals/LoadingModal";
import {ErrorLoadingModal} from "./Modals/ErrorLoadingModal";
import {AddToCartModal} from "./Modals/AddToCartModal";
import {ConsumerViewAvailabilityModal} from "./Modals/ConsumerViewAvailabilityModal";
import {ProductService} from "../../ProductService";
import {selectProperAmountInCart} from "../../redux/cartAmounts/cartAmountSelectors";
import {AddToCartStep, WizardContextProvider} from "./AddToCartWizard";
import {Permission, userHasPermission} from "../../../utils/permissions";
import {getStore} from "~/redux/getStore";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

export type AddToCartSubmitHandler = (descriptor: ProductDescriptor, data: AddToCartFormData) => Promise<boolean>;

export interface AddToCartWizardProps extends AddToCartHookProps {
  restrictionQuery: string;
  closePopup: () => void;
  onSubmit: AddToCartSubmitHandler;
}

export const EditPartWizard = ({ restrictionQuery, closePopup, onSubmit, ...hookProps }: AddToCartWizardProps) => {
  const [formProps, productDescriptor] = useAddToCartForm(hookProps);
  const editMode = hookProps.editMode;

  const [step, setStep] = useState<AddToCartStep>(() => getInitialStep(productDescriptor));

  // Ensure we cycle through initial loading states to a loaded state
  useLayoutEffect(() => {
    setStep(getInitialStep(productDescriptor));
  }, [productDescriptor]);

  const isLoadedState = [AddToCartStep.LOADED_KIT, AddToCartStep.LOADED_DEFAULT].includes(step);

  const submit = async () => {
    const formData = { ...formProps.getValues() };
    const priceReference = formData.priceReference;
    Object.keys(formData.optionalParts).forEach((id) => {
      const part = { ...formData.optionalParts[id] };
      formData.optionalParts[id] = part;
      const { current: descriptor } = ProductService.getProductDescriptor(part.productId, priceReference);
      const totalQuantity =
        part.quantity +
        selectProperAmountInCart({
          productCode: descriptor.base.code,
          packCode: part.selectedPackCode,
          priceReference,
        })(getStore().getState());
      const price = getNetPrice({ product: descriptor, packCode: part.selectedPackCode, totalQuantity });
      part.netPrice = price;
      part.priceUnit = price?.unit ?? 1;
    });

    await onSubmit(productDescriptor, formData);
    closePopup();
  };

  useLayoutEffect(() => {
    if (!productDescriptor.base.quantityVisible && isLoadedState) {
      submit();
    }
  }, [isLoadedState, productDescriptor.base.quantityVisible]);

  const wizardContextProps = {
    closePopup,
    editMode: editMode ?? false,
    restrictionQuery,
    step,
    setStep,
    productDescriptor,
  };

  return (
    <WizardContextProvider {...wizardContextProps}>
      <FormProvider {...formProps}>
        {step === AddToCartStep.LOADING && <LoadingModal />}
        {step === AddToCartStep.ERROR_LOADING && <ErrorLoadingModal productDescriptor={productDescriptor} />}
        {step === AddToCartStep.LOADED_CONSUMER && (
          <ConsumerViewAvailabilityModal productDescriptor={productDescriptor} />
        )}
        {step === AddToCartStep.LOADED_DEFAULT && (
          <AddToCartModal productDescriptor={productDescriptor} submit={submit} />
        )}
      </FormProvider>
    </WizardContextProvider>
  );
};

function getInitialStep(productDescriptor: ProductDescriptor): AddToCartStep {
  if (
    userHasPermission(Permission.AVAILABILITY) &&
    !userHasPermission(Permission.PRICE) &&
    productDescriptor.availability
  ) {
    return AddToCartStep.LOADED_CONSUMER;
  }
  if (productDescriptor.isLoaded) {
    return AddToCartStep.LOADED_DEFAULT;
  }
  if (productDescriptor.isError) {
    return AddToCartStep.ERROR_LOADING;
  }
  return AddToCartStep.LOADING;
}
